import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as AddressIcon,
  Business as BusinessIcon,
  CreditCard as CreditIcon,
  LocalOffer as TagIcon,
  TrendingUp as SalesIcon,
  AccountBalance as BalanceIcon,
  Star as VIPIcon,
  CheckCircle as ActiveIcon,
  Block as InactiveIcon,
  LocationOn as LocationIcon,
  Assignment as DocumentIcon,
} from "@mui/icons-material";

const AddNewCustomer = () => {
  // Sample customers data
  const [customers, setCustomers] = useState([
    {
      id: 1,
      customerCode: "CUST001",
      customerName: "Rajesh Kumar",
      customerType: "Retail",
      email: "rajesh.kumar@email.com",
      phone: "+91 98765 43210",
      city: "Mumbai",
      totalPurchases: 245000,
      outstandingBalance: 15000,
      creditLimit: 50000,
      loyaltyPoints: 2450,
      status: "Active",
      registrationDate: "2023-06-15",
      lastPurchase: "2024-01-18",
    },
    {
      id: 2,
      customerCode: "CUST002",
      customerName: "Priya Sharma",
      customerType: "VIP",
      email: "priya.sharma@email.com",
      phone: "+91 98765 43211",
      city: "Delhi",
      totalPurchases: 550000,
      outstandingBalance: 0,
      creditLimit: 100000,
      loyaltyPoints: 5500,
      status: "Active",
      registrationDate: "2023-03-20",
      lastPurchase: "2024-01-20",
    },
    {
      id: 3,
      customerCode: "CUST003",
      customerName: "Amit Patel",
      customerType: "Wholesale",
      email: "amit.patel@business.com",
      phone: "+91 98765 43212",
      city: "Ahmedabad",
      totalPurchases: 1250000,
      outstandingBalance: 85000,
      creditLimit: 200000,
      loyaltyPoints: 12500,
      status: "Active",
      registrationDate: "2023-01-10",
      lastPurchase: "2024-01-19",
    },
    {
      id: 4,
      customerCode: "CUST004",
      customerName: "Sneha Desai",
      customerType: "Retail",
      email: "sneha.desai@email.com",
      phone: "+91 98765 43213",
      city: "Pune",
      totalPurchases: 125000,
      outstandingBalance: 0,
      creditLimit: 25000,
      loyaltyPoints: 1250,
      status: "Active",
      registrationDate: "2023-08-05",
      lastPurchase: "2024-01-15",
    },
    {
      id: 5,
      customerCode: "CUST005",
      customerName: "Vikram Singh",
      customerType: "Wholesale",
      email: "vikram.singh@enterprise.com",
      phone: "+91 98765 43214",
      city: "Bangalore",
      totalPurchases: 980000,
      outstandingBalance: 45000,
      creditLimit: 150000,
      loyaltyPoints: 9800,
      status: "Inactive",
      registrationDate: "2023-02-28",
      lastPurchase: "2023-12-10",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Form state
  const [customerForm, setCustomerForm] = useState({
    customerCode: "",
    customerName: "",
    customerType: "Retail",
    email: "",
    phone: "",
    alternatePhone: "",
    gstNumber: "",
    panNumber: "",
    // Billing Address
    billingAddress: "",
    billingCity: "",
    billingState: "",
    billingPincode: "",
    billingCountry: "India",
    // Shipping Address
    sameAsBilling: true,
    shippingAddress: "",
    shippingCity: "",
    shippingState: "",
    shippingPincode: "",
    shippingCountry: "India",
    // Business Details
    creditLimit: 0,
    paymentTerms: "Cash",
    creditDays: 0,
    discountPercentage: 0,
    priceListType: "Standard",
    // Additional Info
    dateOfBirth: "",
    anniversary: "",
    referredBy: "",
    remarks: "",
    status: "Active",
    sendWelcomeEmail: true,
  });

  const steps = ["Basic Details", "Contact & Address", "Business Terms", "Additional Info"];

  // Helper functions
  const getFilteredCustomers = () => {
    return customers.filter((customer) => {
      const matchesSearch = Object.values(customer).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || customer.status === statusFilter;
      const matchesType = typeFilter === "all" || customer.customerType === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'VIP': return 'secondary';
      case 'Wholesale': return 'primary';
      case 'Retail': return 'info';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPhone = (phone) => {
    return phone.replace(/(\+\d{2})\s?(\d{5})\s?(\d{5})/, '$1 $2 $3');
  };

  // Handlers
  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      ...customer,
      sameAsBilling: true,
      sendWelcomeEmail: false,
    });
    setActiveStep(0);
    setOpenModal(true);
  };

  const handleView = (customer) => {
    setViewingCustomer(customer);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      setCustomers(customers.filter((customer) => customer.id !== id));
      setSuccessMessage("Customer deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredCustomers().length} customers.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingCustomer(null);
    const newCustomerCode = `CUST${String(customers.length + 1).padStart(3, '0')}`;
    setCustomerForm({
      customerCode: newCustomerCode,
      customerName: "",
      customerType: "Retail",
      email: "",
      phone: "",
      alternatePhone: "",
      gstNumber: "",
      panNumber: "",
      billingAddress: "",
      billingCity: "",
      billingState: "",
      billingPincode: "",
      billingCountry: "India",
      sameAsBilling: true,
      shippingAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingPincode: "",
      shippingCountry: "India",
      creditLimit: 0,
      paymentTerms: "Cash",
      creditDays: 0,
      discountPercentage: 0,
      priceListType: "Standard",
      dateOfBirth: "",
      anniversary: "",
      referredBy: "",
      remarks: "",
      status: "Active",
      sendWelcomeEmail: true,
    });
    setActiveStep(0);
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingCustomer(null);
    setActiveStep(0);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingCustomer(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm({ 
      ...customerForm, 
      [name]: type === 'checkbox' ? checked : value 
    });
    
    // Clear billing address if checkbox is unchecked
    if (name === 'sameAsBilling' && !checked) {
      setCustomerForm({
        ...customerForm,
        sameAsBilling: false,
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!customerForm.customerName) newErrors.customerName = "Customer name is required";
      if (!customerForm.phone) newErrors.phone = "Phone number is required";
      if (!customerForm.email) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(customerForm.email)) newErrors.email = "Invalid email format";
    }
    
    if (step === 1) {
      if (!customerForm.billingAddress) newErrors.billingAddress = "Billing address is required";
      if (!customerForm.billingCity) newErrors.billingCity = "City is required";
      if (!customerForm.billingState) newErrors.billingState = "State is required";
      if (!customerForm.billingPincode) newErrors.billingPincode = "Pincode is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSaveCustomer = () => {
    if (!validateStep(activeStep)) return;

    const newCustomer = {
      id: editingCustomer ? editingCustomer.id : customers.length + 1,
      customerCode: customerForm.customerCode,
      customerName: customerForm.customerName,
      customerType: customerForm.customerType,
      email: customerForm.email,
      phone: customerForm.phone,
      city: customerForm.billingCity,
      totalPurchases: editingCustomer ? editingCustomer.totalPurchases : 0,
      outstandingBalance: editingCustomer ? editingCustomer.outstandingBalance : 0,
      creditLimit: parseFloat(customerForm.creditLimit) || 0,
      loyaltyPoints: editingCustomer ? editingCustomer.loyaltyPoints : 0,
      status: customerForm.status,
      registrationDate: editingCustomer ? editingCustomer.registrationDate : new Date().toISOString().split('T')[0],
      lastPurchase: editingCustomer ? editingCustomer.lastPurchase : "-",
    };

    if (editingCustomer) {
      setCustomers(customers.map(c => c.id === editingCustomer.id ? newCustomer : c));
      setSuccessMessage("Customer updated successfully!");
    } else {
      setCustomers([newCustomer, ...customers]);
      setSuccessMessage("Customer added successfully!");
    }

    handleCloseModal();
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const filteredCustomers = getFilteredCustomers();

  // Calculate statistics
  const activeCustomers = customers.filter(c => c.status === "Active").length;
  const totalSales = customers.reduce((sum, c) => sum + c.totalPurchases, 0);
  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstandingBalance, 0);
  const vipCustomers = customers.filter(c => c.customerType === "VIP").length;

  // Get unique values for filters
  const customerTypes = [...new Set(customers.map(c => c.customerType))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Customer Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your customer database • {filteredCustomers.length} customers found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Add New Customer
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Active Customers</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {activeCustomers}
                  </Typography>
                </Box>
                <ActiveIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Sales</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {formatCurrency(totalSales)}
                  </Typography>
                </Box>
                <SalesIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatCurrency(totalOutstanding)}
                  </Typography>
                </Box>
                <BalanceIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">VIP Customers</Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {vipCustomers}
                  </Typography>
                </Box>
                <VIPIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Customers..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Customer Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Types</option>
              {customerTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Customer Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Customer Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Total Purchases</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Outstanding</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Loyalty Points</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length > 0 ? (
                filteredCustomers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((customer) => (
                    <TableRow 
                      key={customer.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                          {customer.customerCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            {customer.customerName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              {customer.customerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {customer.city}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.customerType} 
                          size="small" 
                          color={getTypeColor(customer.customerType)}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption">{customer.phone}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption">{customer.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary.main">
                          {formatCurrency(customer.totalPurchases)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="600" 
                          color={customer.outstandingBalance > 0 ? "error.main" : "success.main"}
                        >
                          {formatCurrency(customer.outstandingBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.loyaltyPoints} 
                          size="small" 
                          sx={{ 
                            bgcolor: 'warning.light', 
                            color: 'warning.dark',
                            fontWeight: 600 
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={customer.status}
                          size="small"
                          sx={{
                            backgroundColor: customer.status === 'Active' ? '#e8f5e9' : '#ffebee',
                            color: customer.status === 'Active' ? '#2e7d32' : '#d32f2f',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(customer)}
                              sx={{
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.light' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(customer)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { backgroundColor: 'warning.light' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(customer.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No customers found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCustomers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Customer Modal with Stepper */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon />
            {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Basic Details */}
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Code"
                  name="customerCode"
                  value={customerForm.customerCode}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DocumentIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Customer Type *</InputLabel>
                  <Select
                    name="customerType"
                    value={customerForm.customerType}
                    onChange={handleFormChange}
                    label="Customer Type *"
                  >
                    <MenuItem value="Retail">Retail</MenuItem>
                    <MenuItem value="Wholesale">Wholesale</MenuItem>
                    <MenuItem value="VIP">VIP</MenuItem>
                    <MenuItem value="Corporate">Corporate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  name="customerName"
                  value={customerForm.customerName}
                  onChange={handleFormChange}
                  error={!!errors.customerName}
                  helperText={errors.customerName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  name="phone"
                  value={customerForm.phone}
                  onChange={handleFormChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  placeholder="+91 98765 43210"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Alternate Phone"
                  name="alternatePhone"
                  value={customerForm.alternatePhone}
                  onChange={handleFormChange}
                  placeholder="+91 98765 43211"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address *"
                  name="email"
                  type="email"
                  value={customerForm.email}
                  onChange={handleFormChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  name="gstNumber"
                  value={customerForm.gstNumber}
                  onChange={handleFormChange}
                  placeholder="22AAAAA0000A1Z5"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PAN Number"
                  name="panNumber"
                  value={customerForm.panNumber}
                  onChange={handleFormChange}
                  placeholder="ABCDE1234F"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {/* Step 1: Contact & Address */}
          {activeStep === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Billing Address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line *"
                  name="billingAddress"
                  value={customerForm.billingAddress}
                  onChange={handleFormChange}
                  error={!!errors.billingAddress}
                  helperText={errors.billingAddress}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AddressIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City *"
                  name="billingCity"
                  value={customerForm.billingCity}
                  onChange={handleFormChange}
                  error={!!errors.billingCity}
                  helperText={errors.billingCity}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State *"
                  name="billingState"
                  value={customerForm.billingState}
                  onChange={handleFormChange}
                  error={!!errors.billingState}
                  helperText={errors.billingState}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode *"
                  name="billingPincode"
                  value={customerForm.billingPincode}
                  onChange={handleFormChange}
                  error={!!errors.billingPincode}
                  helperText={errors.billingPincode}
                  placeholder="400001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="billingCountry"
                  value={customerForm.billingCountry}
                  onChange={handleFormChange}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Switch
                      name="sameAsBilling"
                      checked={customerForm.sameAsBilling}
                      onChange={handleFormChange}
                    />
                  }
                  label="Shipping address same as billing address"
                />
              </Grid>

              {!customerForm.sameAsBilling && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                      Shipping Address
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line"
                      name="shippingAddress"
                      value={customerForm.shippingAddress}
                      onChange={handleFormChange}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="shippingCity"
                      value={customerForm.shippingCity}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State"
                      name="shippingState"
                      value={customerForm.shippingState}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      name="shippingPincode"
                      value={customerForm.shippingPincode}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      name="shippingCountry"
                      value={customerForm.shippingCountry}
                      onChange={handleFormChange}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {/* Step 2: Business Terms */}
          {activeStep === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Credit Limit"
                  name="creditLimit"
                  type="number"
                  value={customerForm.creditLimit}
                  onChange={handleFormChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    name="paymentTerms"
                    value={customerForm.paymentTerms}
                    onChange={handleFormChange}
                    label="Payment Terms"
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Credit">Credit</MenuItem>
                    <MenuItem value="Net 30">Net 30 Days</MenuItem>
                    <MenuItem value="Net 60">Net 60 Days</MenuItem>
                    <MenuItem value="Advance">Advance Payment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Credit Days"
                  name="creditDays"
                  type="number"
                  value={customerForm.creditDays}
                  onChange={handleFormChange}
                  helperText="Number of days for credit payment"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount %"
                  name="discountPercentage"
                  type="number"
                  value={customerForm.discountPercentage}
                  onChange={handleFormChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Price List Type</InputLabel>
                  <Select
                    name="priceListType"
                    value={customerForm.priceListType}
                    onChange={handleFormChange}
                    label="Price List Type"
                  >
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Wholesale">Wholesale</MenuItem>
                    <MenuItem value="VIP">VIP Pricing</MenuItem>
                    <MenuItem value="Promotional">Promotional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Step 3: Additional Info */}
          {activeStep === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={customerForm.dateOfBirth}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Anniversary Date"
                  name="anniversary"
                  type="date"
                  value={customerForm.anniversary}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Referred By"
                  name="referredBy"
                  value={customerForm.referredBy}
                  onChange={handleFormChange}
                  placeholder="Name of the referrer"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks / Notes"
                  name="remarks"
                  value={customerForm.remarks}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  placeholder="Any additional notes about the customer..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={customerForm.status}
                    onChange={handleFormChange}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="sendWelcomeEmail"
                      checked={customerForm.sendWelcomeEmail}
                      onChange={handleFormChange}
                    />
                  }
                  label="Send welcome email to customer"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              variant="contained" 
              onClick={handleNext}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleSaveCustomer}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {editingCustomer ? 'Update Customer' : 'Add Customer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Customer Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Customer Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingCustomer && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, fontSize: '1.5rem' }}>
                    {viewingCustomer.customerName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {viewingCustomer.customerName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip 
                        label={viewingCustomer.customerType} 
                        color={getTypeColor(viewingCustomer.customerType)}
                        size="small"
                      />
                      <Chip 
                        label={viewingCustomer.status}
                        sx={{
                          backgroundColor: viewingCustomer.status === 'Active' ? '#e8f5e9' : '#ffebee',
                          color: viewingCustomer.status === 'Active' ? '#2e7d32' : '#d32f2f',
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Customer Code</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingCustomer.customerCode}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Registration Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingCustomer.registrationDate}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingCustomer.phone}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingCustomer.email}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Total Purchases</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="primary.main">
                    {formatCurrency(viewingCustomer.totalPurchases)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Outstanding Balance</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="error.main">
                    {formatCurrency(viewingCustomer.outstandingBalance)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Credit Limit</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="warning.main">
                    {formatCurrency(viewingCustomer.creditLimit)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(156, 39, 176, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Loyalty Points</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="secondary.main">
                    {viewingCustomer.loyaltyPoints}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Last Purchase</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingCustomer.lastPurchase}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
          {viewingCustomer && (
            <Button
              variant="contained"
              onClick={() => {
                handleCloseViewModal();
                handleEdit(viewingCustomer);
              }}
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Edit Customer
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddNewCustomer;

